<div class="wrapper flex flex-col justify-between">
  <div class="heading-row flex justify-between items-center mb-[40px]">
    <div class="heading text-type-2 truncate">
      {{ data?.materialTestLabel || data?.materialTestValue || 'Sample Test' }}
    </div>
    <mat-icon class="cursor-pointer" (click)="close()" data-testid="sample-test-close-icon">close</mat-icon>
  </div>

  @if (!sections?.length) {
    <div class="empty-config text-type-8">
      Sample test form is not configured for this organization. Please run the
      sample-test form config SQL for this org.
    </div>
  }

  <div class="details flex-1">
    @for (section of sections; track section.key) {
      <div class="group-accordion" [class.is-expanded]="isSectionExpanded(section.key)">
        <div (click)="toggleSection(section.key)" class="group-header flex justify-between items-center cursor-pointer">
          <div class="group-title text-type-3">{{ section.label | sentenceCase }}</div>
          <mat-icon
            [svgIcon]="isSectionExpanded(section.key) ? 'chevron-up' : 'chevron-down'"
            class="chevron-icon"
          />
        </div>
        <div class="group-content">

          @if (section.kind === 'readonly') {
            <div class="readonly-grid">
              @for (row of section.rows; track row.label) {
                <app-view-field
                  [label]="row.label"
                  [value]="row.value"
                  class="w-full mb-[30px] adjust-label"
                  />
              }
            </div>
          } @else {
            <div class="flat-fields">
              @for (field of section.fields; track trackByName(0, field)) {
                @if (!field?.ui_config?.hidden) {
                  <ng-container *ngTemplateOutlet="fieldRenderer; context: { $implicit: field }" />
                }
              }
              <!-- Child repeating groups (e.g. ATM 315 → Riprap List) declared on
                   the parent agency-view group's ui_config.child_groups. Each one
                   renders as a sub-table inline beneath the section's scalar fields.
                   Rows pack back into result_values.__children__[<context>] on save
                   (matches mobile's onSave shape). -->
              @for (childIdx of section.childGroupIdxs || []; track childIdx) {
                @if (childGroups[childIdx]; as cg) {
                  <div class="child-group mt-[14px]">
                    <div class="child-group-title text-type-11 mb-[8px]">
                      {{ (cg.ref?.label || cg.config?.label || cg.ref.context) | sentenceCase }}
                    </div>
                    <div class="repeating-rows">
                      @if (!cg.rows?.length) {
                        <div class="empty-rows text-type-14 mb-[10px]">No entries yet.</div>
                      }
                      @for (row of cg.rows; track row; let i = $index) {
                        <div class="row-card mb-[12px] p-[12px]">
                          <div class="row-header flex justify-between items-center mb-[8px]">
                            <div class="row-label text-type-11">#{{ i + 1 }}</div>
                            @if (!readOnly) {
                              <button mat-icon-button (click)="removeChildRow(childIdx, i)" aria-label="Remove">
                                <mat-icon svgIcon="trash" />
                              </button>
                            }
                          </div>
                          @for (childField of cg.config?.fields || []; track trackByName(0, childField)) {
                            @if (!childField?.ui_config?.hidden) {
                              <ng-container
                                *ngTemplateOutlet="
                                  childFieldRenderer;
                                  context: { $implicit: childField, row: row, childContext: cg.ref.context, rowIdx: i }
                                "
                              />
                            }
                          }
                        </div>
                      }
                      @if (!readOnly) {
                        <div class="add-item text-type-9 cursor-pointer flex justify-start items-center mb-[10px]">
                          <span (click)="addChildRow(childIdx)" class="flex justify-start items-center">
                            <mat-icon class="add-icon mr-[8px]" svgIcon="plus" />
                            {{ cg.rows?.length ? 'Add another entry' : 'Add entry' }}
                          </span>
                        </div>
                      }
                    </div>
                  </div>
                }
              }
            </div>
          }

        </div>
      </div>
    }

    <!-- Per-test attachments — uploaded via /attachment first; the resulting
         attachment ids ride along in test_overrides.pictures so the api
         links them via entity_type='sample_record_test'. -->
    @if (pictures?.length) {
      <app-pictures-section-with-tags
        (onDelete)="removePicture($event)"
        [isEditable]="!readOnly && !saving"
        [pictures]="pictures"
        class="pictures-section mt-[24px] mb-[24px]"
        label="ATTACHMENTS:"
      />
    }

    <!-- Per-test remarks — applies to every test (mirrors AASHTOWare's
         "Sample Record Test Remarks"). Repeating rows of (type + free text). -->
    <div class="group-accordion" [class.is-expanded]="isSectionExpanded('test_remarks')">
      <div (click)="toggleSection('test_remarks')" class="group-header flex justify-between items-center cursor-pointer">
        <div class="group-title text-type-3">{{ 'Remarks' | sentenceCase }}</div>
        <mat-icon
          [svgIcon]="isSectionExpanded('test_remarks') ? 'chevron-up' : 'chevron-down'"
          class="chevron-icon"
        />
      </div>
      <div class="group-content">
        @if (!remarkRows?.length) {
          <div class="text-type-14 mb-[10px]">No remarks yet.</div>
        }
        @for (row of remarkRows; track row; let i = $index) {
          <div class="row-card mb-[12px] p-[12px]">
            <div class="row-header flex justify-between items-center mb-[8px]">
              <div class="row-label text-type-11">#{{ i + 1 }}</div>
              @if (!readOnly) {
                <button mat-icon-button (click)="removeRemarkRow(i)" aria-label="Remove">
                  <mat-icon>delete_outline</mat-icon>
                </button>
              }
            </div>
            @if (readOnly) {
              <app-view-field
                [value]="remarkTypeLabel(row)"
                class="w-full mb-[20px] adjust-label"
                label="Type"
                />
              <app-view-field
                [value]="row.get('remark')?.value || '-'"
                class="w-full mb-[20px] adjust-label"
                label="Remark"
                />
            } @else {
              <app-dropdown
                [parentFormGroup]="row"
                controlName="remark_type"
                [options]="remarkTypeOptions"
                [enableSearch]="true"
                label="Type"
                class="w-full mb-[20px]"
              />
              <app-text-area
                [parentFormGroup]="row"
                controlName="remark"
                label="Remark"
                class="w-full mb-[20px]"
              />
            }
          </div>
        }
        @if (!readOnly) {
          <div class="add-item text-type-9 cursor-pointer flex justify-start items-center mb-[10px]">
            <span (click)="addRemarkRow()" class="flex justify-start items-center" data-testid="sample-test-add-remark">
              <mat-icon class="add-icon mr-[8px]" svgIcon="plus" />
              {{ remarkRows?.length ? 'Add another remark' : 'Add remark' }}
            </span>
          </div>
        }
      </div>
    </div>

    <!-- Shared field renderer — mirrors the fieldRenderer template in
         create-or-edit-sample-record.component.html so dropdowns / dates /
         multi-select / text inputs all look identical to the parent form. -->
    <ng-template #fieldRenderer let-field>
      @if (isDisplayOnly(field) || readOnly) {
        <app-view-field
          [attr.data-testid]="'sample-test-value-' + field.name"
          [label]="field.label || field.name"
          [value]="displayValueFor(field)"
          class="w-full mb-[30px] adjust-label"
          />
      } @else if (isMultiSelect(field) && fieldHasPickList(field)) {
        <app-multi-select-dropdown
          [parentFormGroup]="form"
          [controlName]="field.name"
          [options]="pickListOptions[field.name] || []"
          [enableSearch]="true"
          [enableSelectAll]="false"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else if (fieldHasPickList(field)) {
        <app-dropdown
          [parentFormGroup]="form"
          [controlName]="field.name"
          [options]="pickListOptions[field.name] || []"
          [enableSearch]="true"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else if (isDate(field)) {
        <app-date-picker
          [parentFormGroup]="form"
          [controlName]="field.name"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else if (isMultiline(field)) {
        <app-text-area
          [parentFormGroup]="form"
          [controlName]="field.name"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else {
        <app-text-input
          [parentFormGroup]="form"
          [controlName]="field.name"
          [label]="field.label || field.name"
          [maxLength]="500"
          class="w-full mb-[20px]"
        />
      }
    </ng-template>

    <!-- Per-child-row field renderer. Mirrors fieldRenderer but binds to the
         child row's FormGroup and reads pick-list options from the per-row
         map keyed by `${childContext}-${rowIdx}-${fieldName}`. -->
    <ng-template #childFieldRenderer let-field let-row="row" let-childContext="childContext" let-rowIdx="rowIdx">
      @if (isDisplayOnly(field) || readOnly) {
        <app-view-field
          [label]="field.label || field.name"
          [value]="childDisplayValueFor(childContext, rowIdx, field, row)"
          class="w-full mb-[20px] adjust-label"
          />
      } @else if (isMultiSelect(field) && fieldHasPickList(field)) {
        <app-multi-select-dropdown
          [parentFormGroup]="row"
          [controlName]="field.name"
          [options]="childRowPickListOptions[childContext + '-' + rowIdx + '-' + field.name] || []"
          [enableSearch]="true"
          [enableSelectAll]="false"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else if (fieldHasPickList(field)) {
        <app-dropdown
          [parentFormGroup]="row"
          [controlName]="field.name"
          [options]="childRowPickListOptions[childContext + '-' + rowIdx + '-' + field.name] || []"
          [enableSearch]="true"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else if (isDate(field)) {
        <app-date-picker
          [parentFormGroup]="row"
          [controlName]="field.name"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else if (isMultiline(field)) {
        <app-text-area
          [parentFormGroup]="row"
          [controlName]="field.name"
          [label]="field.label || field.name"
          class="w-full mb-[20px]"
        />
      } @else {
        <app-text-input
          [parentFormGroup]="row"
          [controlName]="field.name"
          [label]="field.label || field.name"
          [maxLength]="500"
          class="w-full mb-[20px]"
        />
      }
    </ng-template>
  </div>

  <input
    #fileInput
    type="file"
    multiple
    accept="image/*,application/pdf"
    class="hidden-file-input"
    (change)="onFileSelected($event)"
  />

  <div class="action-section mt-[16px] flex flex-row gap-[12px] w-full items-center">
    @if (readOnly) {
      <app-secondary-button
        (handleClick)="close()"
        class="flex-1"
        data-testid="sample-test-close"
      >
        Close
      </app-secondary-button>
    } @else {
      <div [matMenuTriggerFor]="addMenu" class="action-button add-button">
        <mat-icon class="add" svgIcon="plus" />
      </div>
      <app-secondary-button
        (handleClick)="close()"
        [isDisabled]="saving"
        class="flex-1"
      >
        Cancel
      </app-secondary-button>
      <app-primary-button
        (handleClick)="save()"
        [isDisabled]="saving || !sections?.length"
        [isLoading]="saving"
        class="flex-1"
      >
        Save
      </app-primary-button>
    }
  </div>

  <mat-menu #addMenu="matMenu">
    <button (click)="openFilePicker()" mat-menu-item>
      <div class="add-menu-item flex justify-start items-center">
        <div class="flex justify-center items-center h-[48px]">
          <mat-icon svgIcon="paperclip" class="menu-icon" />
        </div>
        <div class="ml-[12px]">Attachments</div>
      </div>
    </button>
  </mat-menu>
</div>
